import { runCreateFolderWorkflow } from "@/action-dialog";
import { useCreateCanvas } from "@/api/canvas";
import { useWorkspaces } from "@/api/workspaces";
import { ROUTES } from "@/constants/routes";
import { useRegisterWorkspaceDetailDropContext } from "@/dnd/TransferDndProvider";
import { WorkspaceDropTarget } from "@/dnd/WorkspaceDropTarget";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import { type Canvas } from "@cohesive/api-client";
import { useDndMonitor } from "@dnd-kit/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  HomeBrowserHeader,
  WORKSPACE_BROWSER_TABS,
} from "../home/browser/HomeBrowserHeader";
import { HomeBrowserList } from "../home/browser/HomeBrowserList";
import { NewMenu } from "../home/browser/NewMenu";
import { useBrowserItemRenderers } from "../home/browser/useBrowserItemRenderers";
import { useBrowserSelectionBar } from "../home/browser/useBrowserSelectionBar";
import { useHomeBrowserData } from "../home/hooks/useHomeBrowserData";
import { useHomeBrowserState } from "../home/hooks/useHomeBrowserState";
import { useHomeViewMode } from "../home/hooks/useHomeViewMode";
import { useWorkspaceBrowserData } from "../home/hooks/useWorkspaceBrowserData";
import {
  browserItemId,
  browserItemSelectionId,
  HOME_BROWSER_LIMIT,
  type BrowserItem,
  type HomeTab,
} from "../home/utils/homeBrowserTypes";
import { homeEmptyCopy } from "../home/utils/homeEmptyCopy";
import {
  browserItemMatchesSearch,
  sortBrowserItems,
} from "../home/utils/homeSort";
import { useChatCardActions } from "../hooks/useChatCardActions";
import { useSelectionKeyboard } from "../hooks/useSelectionKeyboard";
import { useWorkspaceFolderNavigation } from "../hooks/useWorkspaceFolderNavigation";
import { useWorkspaceSelection } from "../hooks/useWorkspaceSelection";
import { useWorkspaceUploadPickers } from "../hooks/useWorkspaceUploadPickers";
import { getItemName } from "../types";
import { getDirectChildren } from "../utils/folderPaths";
import {
  WorkspaceBrowserBreadcrumbs,
  type WorkspaceBrowserCrumb,
} from "./WorkspaceBrowserBreadcrumbs";
import {
  ChatBrowserTree,
  FilesBrowserTree,
  type TreeExpansion,
} from "./WorkspaceBrowserTree";

const HOME_CARD_GRID_TRACK_PX = 270;

/** Typed tabs show the workspace's full resource sets with folder handling. */
const LIBRARY_TAB_LABEL =
  WORKSPACE_BROWSER_TABS.find((t) => t.id === "library")?.label ?? "Files";

const isTypedTab = (tab: HomeTab) => tab === "library" || tab === "chats";

export interface WorkspaceBrowserProps {
  workspaceId: string;
  /** Rendered as the first card/row — upload ghosts on the workspace page. */
  leadingSlot?: React.ReactNode;
  /** File records already represented by an upload ghost; hidden to avoid dupes. */
  suppressedRecordIds?: Set<string>;
  /** Wide-layout toggle state, owned by the surface (it controls the column). */
  wide?: boolean;
  onWideChange?: (wide: boolean) => void;
}

/**
 * The workspace detail page's resource browser. Same tab strip as home with
 * the mixed tab relabeled "Recent" (it keeps home's recents behavior — mixed,
 * folder-free, capped), while Files / Canvases / Chats show ALL workspace
 * resources of that type with full folder handling:
 *
 * - GRID: folder cards first; opening one drills in, tracked per-taxonomy in
 *   URL params (`?path`) with a breadcrumb
 *   trail whose crumbs double as drop targets.
 * - LIST: folders render as inline-expandable rows (session-local expansion;
 *   an active grid drill-in auto-expands its trail so switching views keeps
 *   context).
 *
 * A non-empty search flattens the typed tab to matching items across all
 * folder levels (no folder rows) — searching a tree reads as noise.
 */
export function WorkspaceBrowser({
  workspaceId,
  leadingSlot,
  suppressedRecordIds,
  wide,
  onWideChange,
}: WorkspaceBrowserProps) {
  const navigate = useNavigate();
  const selection = useWorkspaceSelection();
  // Server-computed capability on the workspace response: a viewer sees no
  // create affordances (the registry hides per-record verbs the same way).
  const { data: allWorkspaces } = useWorkspaces();
  const wsCanEdit =
    allWorkspaces?.find((ws) => ws.id === workspaceId)?.can_edit !== false;

  const { dispatch, tab, sort } = useHomeBrowserState("library");
  const [view, setView] = useHomeViewMode(tab, "ws:");
  const [search, setSearch] = useState("");
  const typed = isTypedTab(tab);
  const searching = typed && search.trim().length > 0;

  // ----- folder navigation (grid drill-in) -----
  // One axis: the tree (`?path`) — canvases and files together. Chats are a
  // flat, recency-ordered list with no folders.
  const filesNav = useWorkspaceFolderNavigation();

  // Deep links carry a drill-in param but tab state is component-local, so
  // land on the tab the param belongs to instead of the default.
  useEffect(() => {
    if (filesNav.currentPath) dispatch({ type: "pickTab", tab: "library" });
    // Mount-only: later param changes come from in-page navigation on the
    // already-active tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- data -----
  // Recent + Starred reuse home's recents-style data; the typed tabs read the
  // full workspace sets. The underlying react-query keys overlap, so this
  // costs no duplicate fetches.
  const homeData = useHomeBrowserData(tab, workspaceId);
  const wsData = useWorkspaceBrowserData(tab, workspaceId);

  // ----- current-level folder sets (grid cards / tree roots) -----
  const fileSubfolders = useMemo(
    () =>
      getDirectChildren(wsData.tree, filesNav.currentPath)
        .filter((r) => r.path.endsWith("/") && r.status !== "deleted")
        .sort((a, b) => getItemName(a).localeCompare(getItemName(b))),
    [wsData.tree, filesNav.currentPath],
  );

  // ----- items on the active tab -----
  const items = useMemo<BrowserItem[]>(() => {
    if (!typed) {
      // Recent / Starred: home behavior — mixed, folder-free, capped.
      const visible = homeData.items.filter((item) => {
        if (
          suppressedRecordIds?.size &&
          item.kind === "file" &&
          suppressedRecordIds.has(item.data.id)
        ) {
          return false;
        }
        return browserItemMatchesSearch(item, search);
      });
      return sortBrowserItems(visible, sort).slice(0, HOME_BROWSER_LIMIT);
    }

    let all: BrowserItem[];
    if (tab === "chats") {
      all = wsData.chats.map((data) => ({ kind: "chat", data }));
    } else {
      // The Files tab: everything filed at this level — the canvases and the
      // files together, sorted as one run.
      const rows = searching
        ? wsData.tree
        : getDirectChildren(wsData.tree, filesNav.currentPath);
      all = rows
        .filter(
          (r) =>
            !r.path.endsWith("/") &&
            r.status !== "pending" &&
            r.status !== "deleted" &&
            !suppressedRecordIds?.has(r.id),
        )
        .map((record): BrowserItem | null =>
          record.kind === "canvas"
            ? record.canvas
              ? { kind: "canvas", data: record.canvas }
              : null
            : { kind: "file", data: record },
        )
        .filter((item): item is BrowserItem => item !== null);
    }
    return sortBrowserItems(
      all.filter((item) => browserItemMatchesSearch(item, search)),
      sort,
    );
  }, [
    typed,
    tab,
    searching,
    homeData.items,
    wsData.tree,
    wsData.chats,
    filesNav.currentPath,
    suppressedRecordIds,
    search,
    sort,
  ]);

  // Folder open (files): clear stale selection, then jump to the folder's
  // full path. Not `pushSegment` — the list tree can open folders nested
  // below the current level, where appending one segment would mis-navigate.
  const clearSelection = selection.clear;
  const handleFolderOpen = useCallback(
    (record: FileRecord) => {
      if (!record.path.endsWith("/")) return;
      clearSelection();
      filesNav.navigateToPath(record.path);
    },
    [clearSelection, filesNav],
  );

  // ----- renderers -----

  // ----- chat/canvas creation (files new items into the current folder) -----
  const chatCardActions = useChatCardActions({ workspaceId });
  const createCanvas = useCreateCanvas({
    onError: () => toast.error("Failed to create canvas"),
  });
  // Creating while inside a folder files the new canvas there in the same
  // request — the canvas and its place in the tree are written together.
  const handleCreateCanvas = useCallback(
    (parentPath: string = filesNav.currentPath) => {
      if (createCanvas.isPending) return;
      createCanvas.mutate(
        { workspaceId, parent_path: parentPath },
        {
          onSuccess: (newCanvas) => {
            navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: newCanvas.id }));
          },
        },
      );
    },
    [workspaceId, filesNav.currentPath, createCanvas, navigate],
  );

  const { uploadQueue, uploadInputs, uploadFilesTo, uploadFolderTo } =
    useWorkspaceUploadPickers(workspaceId);
  const openFileRecord = useOpenFileRecord();

  // One set of folder-scoped create/upload handlers for every page surface a
  // folder renders on (cards, list rows, tree rows) — the same verbs, in the
  // same registry menu, as the rail's folder rows.
  const folderExtraHandlers = useCallback(
    (record: FileRecord): ObjectActionHandlers => ({
      "new-canvas": () => handleCreateCanvas(record.path),
      "new-document": () =>
        window.setTimeout(
          () =>
            void uploadQueue.createDoc(record.path, {
              onCreated: (created) => openFileRecord(created.id),
            }),
          0,
        ),
      "new-folder": () =>
        window.setTimeout(
          () =>
            void runCreateFolderWorkflow({
              workspaceId,
              currentPath: record.path,
            }),
          0,
        ),
      "upload-files": () =>
        window.setTimeout(() => uploadFilesTo(record.path), 0),
      "upload-folder": () =>
        window.setTimeout(() => uploadFolderTo(record.path), 0),
    }),
    [
      handleCreateCanvas,
      uploadQueue,
      openFileRecord,
      workspaceId,
      uploadFilesTo,
      uploadFolderTo,
    ],
  );

  const {
    renderGridItem,
    renderRow,
    renderFolderCard,
    canvasActions,
    fileActions,
    chatActions,
  } = useBrowserItemRenderers({
    workspaceId,
    filesInScope: wsData.tree,
    draggable: true,
    onFolderOpen: handleFolderOpen,
    folderExtraHandlers,
  });

  const fileFolderChildCount = useCallback(
    (record: FileRecord) =>
      getDirectChildren(wsData.tree, record.path).filter(
        (r) => r.status !== "pending" && r.status !== "deleted",
      ).length,
    [wsData.tree],
  );

  // ----- grid folder cards at the current level (tree tab only) -----
  const folderCards = useMemo<React.ReactNode[] | undefined>(() => {
    if (!typed || searching || view !== "grid" || tab !== "library") {
      return undefined;
    }
    return fileSubfolders.map((record) => renderFolderCard(record, 0));
  }, [typed, searching, view, tab, fileSubfolders, renderFolderCard]);

  // ----- list-view inline folder tree -----
  // Session-local expansion per tab; keys are folder ids (canvas/chat) or
  // folder paths (files).
  const [expandedByTab, setExpandedByTab] = useState<
    Record<string, Set<string>>
  >({});
  const expansion = useMemo<TreeExpansion>(() => {
    const current = expandedByTab[tab] ?? new Set<string>();
    return {
      isOpen: (key) => current.has(key),
      onToggle: (key) =>
        setExpandedByTab((prev) => {
          const next = new Set(prev[tab] ?? []);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return { ...prev, [tab]: next };
        }),
    };
  }, [expandedByTab, tab]);

  const sortItems = useCallback(
    (arr: BrowserItem[]) => sortBrowserItems(arr, sort),
    [sort],
  );
  const renderTreeRow = useCallback(
    (item: BrowserItem, depth: number) =>
      depth > 0 ? (
        <div key={browserItemId(item)} style={{ paddingLeft: depth * 20 }}>
          {renderRow(item)}
        </div>
      ) : (
        renderRow(item)
      ),
    [renderRow],
  );

  // The tree roots at the current drill-in level — same scope the grid shows,
  // so a double-click drill-in (or a crumb) behaves identically across views;
  // single-click expansion then explores beneath that level inline.
  const listBody = useMemo<React.ReactNode>(() => {
    if (!typed || searching || view !== "list") return undefined;
    // An empty tree yields no rows, and its presence suppresses the shared
    // empty state — fall back to the default (empty) body instead. A level
    // with neither folders nor loose items has no subtree content either.
    if (tab === "chats") {
      return (
        <ChatBrowserTree
          chats={wsData.chats}
          workspaceId={workspaceId}
          sortItems={sortItems}
          renderRow={renderTreeRow}
        />
      );
    }
    if (fileSubfolders.length === 0 && items.length === 0) return undefined;
    return (
      <FilesBrowserTree
        records={wsData.tree}
        parentPath={filesNav.currentPath}
        workspaceId={workspaceId}
        expansion={expansion}
        suppressedRecordIds={suppressedRecordIds}
        sortItems={sortItems}
        renderRow={renderTreeRow}
        fileFolderDeps={{
          onOpen: handleFolderOpen,
          onRename: fileActions.rename,
          onDelete: fileActions.delete,
        }}
        fileFolderExtraHandlers={folderExtraHandlers}
        folderChildCount={fileFolderChildCount}
      />
    );
  }, [
    typed,
    searching,
    view,
    tab,
    workspaceId,
    items.length,
    fileSubfolders.length,
    wsData.chats,
    wsData.tree,
    filesNav.currentPath,
    expansion,
    sortItems,
    renderTreeRow,
    fileFolderChildCount,
    handleFolderOpen,
    fileActions,
    suppressedRecordIds,
  ]);

  // ----- breadcrumbs (grid drill-in) -----
  const crumbs = useMemo<WorkspaceBrowserCrumb[]>(() => {
    if (!typed || searching) return [];
    if (tab === "library") {
      if (filesNav.segments.length === 0) return [];
      const wrapInDropTarget =
        (parentPath: string) => (children: React.ReactNode) => (
          <WorkspaceDropTarget
            workspaceId={workspaceId}
            parentPath={parentPath}
            className="rounded-sm"
          >
            {children}
          </WorkspaceDropTarget>
        );
      return [
        {
          key: "__root__",
          // The root crumb is the tab the tree lives on, so the trail reads
          // "Files / Research / Q3" — one label, defined once with the tab.
          label: LIBRARY_TAB_LABEL,
          onNavigate: () => filesNav.navigateToPath(""),
          wrap: wrapInDropTarget(""),
        },
        ...filesNav.segments.map((seg, i) => {
          const segmentPath = filesNav.segments.slice(0, i + 1).join("/") + "/";
          const isLast = i === filesNav.segments.length - 1;
          return {
            key: segmentPath,
            label: seg,
            onNavigate: isLast
              ? undefined
              : () => filesNav.navigateToPath(segmentPath),
            wrap: isLast ? undefined : wrapInDropTarget(segmentPath),
          };
        }),
      ];
    }

    // Chats are flat — no trail.
    return [];
  }, [typed, searching, tab, filesNav, workspaceId]);

  // ----- selection plumbing -----
  const orderedSelectionIds = useMemo(
    () =>
      items
        .map(browserItemSelectionId)
        .filter((id): id is string => id !== null),
    [items],
  );
  useEffect(() => {
    selection.setOrderedIds(orderedSelectionIds);
  }, [orderedSelectionIds, selection]);

  const selectionResolutionItems = useMemo<BrowserItem[]>(
    () => [
      ...homeData.items,
      ...wsData.canvases.map((data): BrowserItem => ({ kind: "canvas", data })),
      ...wsData.tree
        .filter((record) => record.kind !== "canvas")
        .map((data): BrowserItem => ({ kind: "file", data })),
      ...wsData.chats.map((data): BrowserItem => ({ kind: "chat", data })),
    ],
    [homeData.items, wsData.canvases, wsData.tree, wsData.chats],
  );
  const { selectionBar, openSelectedId } = useBrowserSelectionBar({
    items: selectionResolutionItems,
    workspaceId,
    allRecords: wsData.tree,
    canvasActions,
    fileActions,
    openChat: chatActions.open,
    onFolderOpen: handleFolderOpen,
  });
  useSelectionKeyboard(openSelectedId);

  // ----- drag dispatch: register this page as a workspace-detail source -----
  const canvasMap = useMemo(() => {
    const m = new Map<string, Canvas>();
    for (const c of wsData.canvases) m.set(c.id, c);
    return m;
  }, [wsData.canvases]);
  const fileRecordMap = useMemo(() => {
    const m = new Map<string, FileRecord>();
    for (const r of wsData.tree) m.set(r.id, r);
    return m;
  }, [wsData.tree]);
  useRegisterWorkspaceDetailDropContext(
    useMemo(
      () => ({
        sourceWorkspaceId: workspaceId,
        canvasMap,
        fileRecordMap,
        // Provenance for drags off this page, so a drop back onto the folder
        // being viewed is a no-op instead of an idempotent move.
        currentPath: filesNav.currentPath,
      }),
      [workspaceId, canvasMap, fileRecordMap, filesNav.currentPath],
    ),
  );
  // A drag consumed the selection (it travels with the payload) — clear it.
  useDndMonitor({ onDragEnd: () => selection.clear() });

  // ----- "+ New" -----
  const onNewFolder = useMemo(() => {
    if (!typed) return undefined;
    if (tab !== "library") return undefined;
    return () =>
      void runCreateFolderWorkflow({
        workspaceId,
        currentPath: filesNav.currentPath,
      });
  }, [typed, tab, workspaceId, filesNav.currentPath]);

  const activeData = typed ? wsData : homeData;

  // Inside a drilled-into folder the tab-level empty copy ("No canvases
  // yet…") would be wrong — the workspace has content, this folder doesn't.
  // Mirror the rail's folder empty state instead; "+ New" and drag-drop
  // still target the folder being viewed.
  const inFolder = tab === "library" && !!filesNav.currentPath;
  const emptyCopy =
    typed && inFolder
      ? {
          title: "This folder is empty.",
          hint: "Create something with + New or drag items in",
        }
      : homeEmptyCopy(tab, true);

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      {uploadInputs}
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-medium text-n-90">Workspace Activity</h2>
        {selectionBar}
      </div>
      <HomeBrowserHeader
        tabs={WORKSPACE_BROWSER_TABS}
        tab={tab}
        onTabChange={(next) => dispatch({ type: "pickTab", tab: next })}
        view={view}
        onViewChange={setView}
        sort={sort}
        onSortChange={(next) => dispatch({ type: "setSort", sort: next })}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search this workspace"
        wide={wide}
        onWideChange={onWideChange}
        newMenu={
          wsCanEdit ? (
            <NewMenu
              onNewCanvas={() =>
                handleCreateCanvas(
                  tab === "library" ? filesNav.currentPath : "",
                )
              }
              onNewChat={() => chatCardActions.onCreate()}
              onNewFolder={onNewFolder}
              isCreating={createCanvas.isPending || chatCardActions.isCreating}
            />
          ) : undefined
        }
      />
      {crumbs.length > 1 && <WorkspaceBrowserBreadcrumbs crumbs={crumbs} />}
      <HomeBrowserList
        view={view}
        items={items}
        isPending={activeData.isPending}
        isError={activeData.isError}
        onRetry={activeData.refetch}
        empty={emptyCopy}
        renderGridItem={renderGridItem}
        renderRow={renderRow}
        gridTrackPx={HOME_CARD_GRID_TRACK_PX}
        sort={sort}
        onSortFieldChange={(field) => dispatch({ type: "sortByField", field })}
        folderCards={folderCards}
        listBody={listBody}
        leadingSlot={leadingSlot}
      />
    </section>
  );
}
